import { ANTIFRAUD_LISTS, ANTIFRAUD_RULES } from 'shared/components/ActivitiesPermissionsBasedAccessValidator/constants'
import { AWS_S3_URL, PANEL_URL } from 'utils/constants'
import { IItem, TIconByCompany } from './interface'
import { TActivityPermission } from 'shared/components/ActivitiesPermissionsBasedAccessValidator/interface'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

export const LINKS_FOOTER_FOR_HOME = (): IItem[] => {
  const { t } = useTranslation()
  const T_ROOT = 'navigator_footer_for_home_links'
  return [
    {
      label: t(`${T_ROOT}.help_center`),
      to: 'https://help.conekta.com/hc/es-419'
    },
    {
      label: t(`${T_ROOT}.documentation`),
      to: 'https://developers.conekta.com/'
    },
    {
      label: t(`${T_ROOT}.api_reference`),
      to: 'https://developers.conekta.com/api'
    }
  ]
}

export const MENU_ITEMS = (idReferenceCompany = '', flags: Record<string, boolean>, t: TFunction): Partial<IItem>[] => {
  const T_ROOT = 'mainMenu'
  const { antifraudActive, cashoutModuleActive } = flags
  const menus: Partial<IItem>[] = [
    {
      activityPermission: 'dashboards:read' as TActivityPermission,
      iconLeft: `${ROOT_NAV_ICONS}/dashboard-24px.svg`,
      label: t(`${T_ROOT}.dashboard`),
      rootPath: 'dashboard',
      to: `/dashboard`
    },
    {
      activityPermission: 'orders:read' as TActivityPermission,
      iconLeft: `${ROOT_NAV_ICONS}/payments-24px.svg`,
      label: t(`${T_ROOT}.transactions`),
      rootPath: 'transactions',
      submenu: [
        {
          activityPermission: 'orders:read' as TActivityPermission,
          label: t(`${T_ROOT}.payments`),
          rootPath: 'payments',
          to: `/transactions/payments`
        },
        {
          activityPermission: 'transfers:read' as TActivityPermission,
          label: t(`${T_ROOT}.transfers`),
          rootPath: 'transfers',
          to: `/transactions/transfers`
        },
        {
          activityPermission: 'chargebacks:read' as TActivityPermission,
          label: t(`${T_ROOT}.chargebacks`),
          rootPath: 'chargebacks',
          to: `/transactions/chargebacks`
        }
      ]
    },
    {
      activityPermission: 'payment_link:read' as TActivityPermission,
      iconLeft: `${ROOT_NAV_ICONS}/share-link-24px.svg`,
      label: t(`${T_ROOT}.paymentLinks`),
      rootPath: 'paymentLinks',
      to: `/payment-links/${idReferenceCompany}`
    },
    {
      activityPermission: 'receipts:read' as TActivityPermission,
      iconLeft: `${ROOT_NAV_ICONS}/timeline-24px.svg`,
      label: t(`${T_ROOT}.accounting`),
      rootPath: 'accounting',
      submenu: [
        {
          activityPermission: 'receipts:read' as TActivityPermission,
          label: t(`${T_ROOT}.billing`),
          rootPath: 'receipts',
          to: `/accounting/receipts`
        },
        {
          activityPermission: 'ledger_entries:read' as TActivityPermission,
          label: t(`${T_ROOT}.accountStatement`),
          rootPath: 'statement',
          to: `/accounting/statement`
        }
      ]
    },
    {
      activityPermission: 'customers:read' as TActivityPermission,
      iconLeft: `${ROOT_NAV_ICONS}/business-24px.svg`,
      label: t(`${T_ROOT}.customersAndPlans`),
      rootPath: 'subscriptions',
      submenu: [
        {
          activityPermission: 'customers:read' as TActivityPermission,
          label: t(`${T_ROOT}.customers`),
          rootPath: 'customers',
          to: `/subscriptions/customers`
        },
        {
          activityPermission: 'plans:read' as TActivityPermission,
          label: t(`${T_ROOT}.plans`),
          rootPath: 'plans',
          to: `/subscriptions/plans`
        }
      ]
    }
  ]

  if (antifraudActive) {
    menus.splice(2, 0, MENU_ITEM_FOR_ANTIFRAUD(flags, t))
  }

  if (cashoutModuleActive) {
    menus.splice(3, 0, MENU_ITEM_FOR_CASHOUT(flags, t))
  }

  return menus
}

const MENU_ITEM_FOR_ANTIFRAUD = (flags: Record<string, boolean>, t: TFunction): Partial<IItem> => {
  const T_ROOT = 'mainMenu'
  const { listsModuleActive, rulesModuleActive, secure3dsModuleActive } = flags
  const menu: Partial<IItem> = {
    iconLeft: `${ROOT_NAV_ICONS}/antifraud-filled-white-24px.svg`,
    label: t(`${T_ROOT}.antifraud`),
    rootPath: 'antifraud',
    submenu: []
  }

  if (listsModuleActive) {
    menu.submenu?.push({
      activityPermission: ANTIFRAUD_LISTS.READ,
      isBeta: true,
      label: t(`${T_ROOT}.lists`),
      rootPath: 'lists',
      to: PANEL_URL.LISTS
    })
  }

  if (rulesModuleActive) {
    menu.submenu?.push({
      activityPermission: ANTIFRAUD_RULES.READ,
      isBeta: true,
      label: t(`${T_ROOT}.rules`),
      rootPath: 'rules',
      to: PANEL_URL.RULES
    })
  }

  if (secure3dsModuleActive) {
    menu.submenu?.push({
      label: t(`${T_ROOT}.secure3ds`),
      rootPath: 'secure3ds',
      to: PANEL_URL.SECURE_3DS
    })
  }

  return menu
}

const MENU_ITEM_FOR_CASHOUT = (flags: Record<string, boolean>, t: TFunction): Partial<IItem> => {
  const T_ROOT = 'mainMenu'

  return {
    activityPermission: 'orders:read',
    iconLeft: `${ROOT_NAV_ICONS}/cashout-nav.svg`,
    label: t(`${T_ROOT}.cashout`),
    rootPath: 'cashout',
    to: `/cashout`
  }
}

export const MENU_ITEMS_FOR_HOME = (): IItem[] => {
  const { t } = useTranslation()
  const T_ROOT = 'mainMenuForHome'
  return [
    {
      iconLeft: `${ROOT_NAV_ICONS}/my_company.svg`,
      label: t(`${T_ROOT}.myCompanies`),
      rootPath: 'myCompanies',
      skipActivityPermission: true,
      to: '/'
    }
  ]
}

export const MENU_ITEMS_FOR_NAV_FOOTER = (): IItem[] => {
  const { t } = useTranslation()
  const T_ROOT = 'mainMenu'
  return [
    {
      iconLeft: `${ROOT_NAV_ICONS}/integration-instructions-24px.svg`,
      label: t(`${T_ROOT}.developers`),
      rootPath: 'developers',
      to: `/developers`
    },
    {
      activityPermission: 'settings:read',
      iconLeft: `${ROOT_NAV_ICONS}/settings-24px.svg`,
      label: t(`${T_ROOT}.settings`),
      rootPath: 'settings',
      to: `/settings`
    },
    {
      external: true,
      externalToAdmin: true,
      iconLeft: `${ROOT_NAV_ICONS}/help-16px.svg`,
      label: t(`${T_ROOT}.helpCenter`),
      to: t(`${T_ROOT}.helpCenterLink`)
    }
  ]
}

export const ROOT_NAV_ICONS = `${AWS_S3_URL}/img/nav`

export const ICONS_BY_COMPANY_TYPE: TIconByCompany = {
  activated: '',
  active: `${ROOT_NAV_ICONS}/company_active.svg`,
  inProcess: `${ROOT_NAV_ICONS}/company_inprocess.svg`,
  inactive: `${ROOT_NAV_ICONS}/company_inactive.svg`,
  testing: `${ROOT_NAV_ICONS}/company_dev.svg`
}
